import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch, getToken, setToken } from '../api';

export interface AuthUser {
  id: string;
  email: string;
  roles: string[];
  firstName?: string;
  lastName?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  /** true while the stored token is being validated on first load */
  initializing: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  initializing: true,
  login: async () => 'Auth not ready',
  register: async () => 'Auth not ready',
  logout: () => {},
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Validate any stored token on mount so a stale/expired token doesn't
  // present as logged-in.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setInitializing(false);
      return;
    }
    apiFetch('/api/auth/me')
      .then(async (res) => {
        if (res.ok) {
          setUser(await res.json());
        } else {
          setToken(null);
        }
      })
      .catch(() => setToken(null))
      .finally(() => setInitializing(false));
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return body.error || 'Login failed';
      setToken(body.token);
      setUser(body.user);
      return null;
    } catch {
      return 'Could not reach the server';
    }
  }, []);

  const register = useCallback(async (email: string, password: string, firstName: string, lastName: string): Promise<string | null> => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, firstName, lastName }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return body.error || 'Registration failed';
      // Log straight in after a successful registration.
      return login(email, password);
    } catch {
      return 'Could not reach the server';
    }
  }, [login]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, initializing, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
